/**
 * Layout for reward charts: sticker grids, weekly behaviour charts and token boards.
 * Positions are millimetres on a page from `pageGeometry`; every chart is made of shape
 * marks (an outline for a sticker or a tick) and a few text items.
 */

import type { PageGeometry } from "@/lib/tool-kit/printables/page";
import { r2 } from "@/lib/tool-kit/printables/page";
import {
  type FontSpec,
  fitLines,
  type TextMeasure,
} from "@/lib/tool-kit/printables/text";

export type RewardShape = "circle" | "square" | "star" | "heart";
export type StickerSize = "small" | "medium" | "large";

export const MAX_BOXES = 60;
export const MIN_BOXES = 4;
export const MAX_GOALS = 10;

/** Sticker box widths: standard 3/4 in dot stickers fit the small box; larger stickers fit above. */
export const STICKER_BOX_MM: Record<StickerSize, number> = {
  small: 15,
  medium: 20,
  large: 25,
};

export interface ShapeMark {
  cx: number;
  cy: number;
  size: number;
  /** A faint number inside the shape. */
  number?: number;
  /** The last box of a sticker chart, drawn emphasised. */
  final?: boolean;
}

export interface PlacedText {
  text: string;
  x: number;
  y: number;
  size: number;
  anchor: "start" | "middle" | "end";
  bold?: boolean;
}

export interface Rule {
  x1: number;
  x2: number;
  y: number;
}

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

const BOLD: FontSpec = { kind: "sans", bold: true };
const SANS: FontSpec = { kind: "sans" };

function text(
  value: string,
  x: number,
  y: number,
  size: number,
  anchor: PlacedText["anchor"] = "start",
  bold = false
): PlacedText {
  return { text: value, x: r2(x), y: r2(y), size: r2(size), anchor, bold };
}

export interface HeaderLayout {
  title: PlacedText[];
  /** "Name ____" with its rule. */
  name: { label: PlacedText; rule: Rule } | null;
  /** "Working for: ____" with the reward text on the rule, if given. */
  goal: { label: PlacedText; rule: Rule; value: PlacedText | null } | null;
  height: number;
}

export interface HeaderOptions {
  title: string;
  nameLabel: string;
  goalLabel: string;
  reward: string;
  showName: boolean;
  showGoal: boolean;
}

/** Title, name line and reward line across the top of a page. */
export function layoutHeader(
  geometry: PageGeometry,
  measure: TextMeasure,
  options: HeaderOptions
): HeaderLayout {
  const left = geometry.left;
  const right = geometry.left + geometry.contentWidth;
  const titleBlock = options.title.trim()
    ? fitLines(
        measure,
        options.title.trim(),
        BOLD,
        geometry.contentWidth,
        13,
        6,
        1
      )
    : null;
  const title = titleBlock
    ? titleBlock.lines.map((line) =>
        text(
          line,
          left,
          geometry.top + titleBlock.size * 0.85,
          titleBlock.size,
          "start",
          true
        )
      )
    : [];
  let y = geometry.top + (titleBlock ? titleBlock.size * 1.35 : 0);
  let name: HeaderLayout["name"] = null;
  if (options.showName) {
    const labelWidth = measure(options.nameLabel, SANS) * 4.2;
    name = {
      label: text(options.nameLabel, left, y + 6, 4.2),
      rule: {
        x1: r2(left + labelWidth + 2),
        x2: r2(left + geometry.contentWidth * 0.5),
        y: r2(y + 6.8),
      },
    };
  }
  let goal: HeaderLayout["goal"] = null;
  if (options.showGoal) {
    const labelWidth = measure(options.goalLabel, SANS) * 4.2;
    const startX = options.showName
      ? left + geometry.contentWidth * 0.55
      : left;
    const ruleX1 = startX + labelWidth + 2;
    goal = {
      label: text(options.goalLabel, startX, y + 6, 4.2),
      rule: { x1: r2(ruleX1), x2: r2(right), y: r2(y + 6.8) },
      value: options.reward.trim()
        ? text(options.reward.trim(), ruleX1 + 1, y + 5.8, 4.4, "start", true)
        : null,
    };
  }
  if (options.showName || options.showGoal) {
    y += 12;
  }
  return { title, name, goal, height: r2(y - geometry.top) };
}

/* -------------------------------------------------------------------- sticker */

export interface StickerLayout {
  header: HeaderLayout;
  marks: ShapeMark[];
  /** The box (pitch) actually used, which may be smaller than asked for. */
  box: number;
  cols: number;
  rows: number;
  /** False when even the smallest box could not hold every sticker. */
  fits: boolean;
}

const GAP_RATIO = 0.22;
const MIN_BOX = 9;

/** The grid of numbered boxes, shrunk step by step until it fits the page. */
export function layoutSticker(
  geometry: PageGeometry,
  measure: TextMeasure,
  header: HeaderOptions,
  count: number,
  size: StickerSize
): StickerLayout {
  const head = layoutHeader(geometry, measure, header);
  const top = geometry.top + head.height + 4;
  const availableHeight = geometry.top + geometry.contentHeight - top;
  let box = STICKER_BOX_MM[size];
  let cols = 1;
  let rows = count;
  let fits = false;
  for (let attempt = 0; attempt < 200; attempt += 1) {
    const pitch = box * (1 + GAP_RATIO);
    cols = Math.max(
      1,
      Math.floor((geometry.contentWidth + box * GAP_RATIO) / pitch)
    );
    rows = Math.ceil(count / cols);
    if (rows * pitch - box * GAP_RATIO <= availableHeight + 0.01) {
      fits = true;
      break;
    }
    if (box <= MIN_BOX) {
      break;
    }
    box = Math.max(MIN_BOX, box - 0.5);
  }
  const pitch = box * (1 + GAP_RATIO);
  const gridWidth = cols * pitch - box * GAP_RATIO;
  const left = geometry.left + (geometry.contentWidth - gridWidth) / 2;
  const marks: ShapeMark[] = Array.from({ length: count }, (_, index) => {
    const row = Math.floor(index / cols);
    const col = index % cols;
    return {
      cx: r2(left + col * pitch + box / 2),
      cy: r2(top + row * pitch + box / 2),
      size: r2(box),
      number: index + 1,
      final: index === count - 1,
    };
  });
  return { header: head, marks, box: r2(box), cols, rows, fits };
}

/* ------------------------------------------------------------------- behaviour */

export interface BehaviorLayout {
  header: HeaderLayout;
  days: { text: PlacedText; x: number; width: number }[];
  goals: { label: PlacedText[]; y: number; height: number }[];
  marks: ShapeMark[];
  total: { x: number; width: number; head: PlacedText } | null;
  table: Rect;
  labelWidth: number;
  /** y of the horizontal rules: under the heading and under each row. */
  rules: number[];
  /** x of the vertical rules. */
  columns: number[];
  footer: { rewardLine: PlacedText; rule: Rule } | null;
}

export interface BehaviorOptions {
  header: HeaderOptions;
  goals: readonly string[];
  dayNames: readonly string[];
  total: boolean;
  totalLabel: string;
  footerLabel: string;
}

/** Goals down the side, days across, a shape to mark in every cell. */
export function layoutBehavior(
  geometry: PageGeometry,
  measure: TextMeasure,
  options: BehaviorOptions
): BehaviorLayout {
  const header = layoutHeader(geometry, measure, options.header);
  const goals = options.goals.slice(0, MAX_GOALS);
  const rowsCount = Math.max(1, goals.length);
  const top = geometry.top + header.height + 4;
  const footerHeight = options.footerLabel.trim() ? 16 : 0;
  const bottom = geometry.top + geometry.contentHeight - footerHeight;
  const headHeight = 10;
  const rowHeight = Math.max(
    12,
    Math.min(26, (bottom - top - headHeight) / rowsCount)
  );
  const dayCount = Math.max(1, options.dayNames.length);
  const labelWidth = Math.min(70, geometry.contentWidth * 0.34);
  const totalWidth = options.total ? 18 : 0;
  const dayWidth = (geometry.contentWidth - labelWidth - totalWidth) / dayCount;
  const left = geometry.left;
  const tableHeight = headHeight + rowHeight * rowsCount;
  const columns = [
    r2(left),
    r2(left + labelWidth),
    ...options.dayNames.map((_, i) =>
      r2(left + labelWidth + dayWidth * (i + 1))
    ),
  ];
  if (options.total) {
    columns.push(r2(left + geometry.contentWidth));
  }
  const days = options.dayNames.map((name, i) => ({
    text: text(
      name,
      left + labelWidth + dayWidth * (i + 0.5),
      top + 6.5,
      3.8,
      "middle",
      true
    ),
    x: r2(left + labelWidth + dayWidth * i),
    width: r2(dayWidth),
  }));
  const goalRows = Array.from({ length: rowsCount }, (_, row) => {
    const y = top + headHeight + row * rowHeight;
    const label = goals[row]
      ? fitLines(measure, goals[row] ?? "", BOLD, labelWidth - 6, 4.2, 2.8, 3)
      : { size: 4, lines: [] as string[] };
    const lineHeight = label.size * 1.25;
    const first =
      y +
      rowHeight / 2 -
      ((label.lines.length - 1) * lineHeight) / 2 +
      label.size * 0.35;
    return {
      label: label.lines.map((line, i) =>
        text(line, left + 3, first + i * lineHeight, label.size, "start", true)
      ),
      y: r2(y),
      height: r2(rowHeight),
    };
  });
  const shape = Math.min(dayWidth, rowHeight) * 0.6;
  const marks: ShapeMark[] = [];
  for (let row = 0; row < rowsCount; row += 1) {
    for (let day = 0; day < dayCount; day += 1) {
      marks.push({
        cx: r2(left + labelWidth + dayWidth * (day + 0.5)),
        cy: r2(top + headHeight + rowHeight * (row + 0.5)),
        size: r2(shape),
      });
    }
  }
  const totalColumn = options.total
    ? {
        x: r2(left + geometry.contentWidth - totalWidth),
        width: totalWidth,
        head: text(
          options.totalLabel,
          left + geometry.contentWidth - totalWidth / 2,
          top + 6.5,
          3.8,
          "middle",
          true
        ),
      }
    : null;
  const footer = options.footerLabel.trim()
    ? {
        rewardLine: text(
          options.footerLabel.trim(),
          left,
          top + tableHeight + 11,
          4.2
        ),
        rule: {
          x1: r2(left + measure(options.footerLabel.trim(), SANS) * 4.2 + 2),
          x2: r2(left + geometry.contentWidth),
          y: r2(top + tableHeight + 11.8),
        },
      }
    : null;
  return {
    header,
    days,
    goals: goalRows,
    marks,
    total: totalColumn,
    table: {
      x: r2(left),
      y: r2(top),
      width: r2(geometry.contentWidth),
      height: r2(tableHeight),
    },
    labelWidth: r2(labelWidth),
    rules: [
      r2(top + headHeight),
      ...goalRows.map((row) => r2(row.y + row.height)),
    ],
    columns,
    footer,
  };
}

/* ----------------------------------------------------------------------- token */

export interface TokenBoard {
  box: Rect;
  title: PlacedText | null;
  marks: ShapeMark[];
  reward: { rect: Rect; label: PlacedText; value: PlacedText[] };
}

export interface TokenOptions {
  title: string;
  tokens: number;
  rewardLabel: string;
  reward: string;
}

export const BOARD_COUNTS = [1, 2, 3, 4] as const;
export type BoardCount = (typeof BOARD_COUNTS)[number];

/** Boards stacked down the page, each a title, a row of tokens and a reward box. */
export function layoutTokenBoards(
  geometry: PageGeometry,
  measure: TextMeasure,
  options: TokenOptions,
  boards: BoardCount
): TokenBoard[] {
  const height = geometry.contentHeight / boards;
  const result: TokenBoard[] = [];
  for (let i = 0; i < boards; i += 1) {
    const box: Rect = {
      x: geometry.left,
      y: r2(geometry.top + i * height),
      width: r2(geometry.contentWidth),
      height: r2(height),
    };
    const pad = Math.min(8, height * 0.08);
    const innerWidth = box.width - pad * 2;
    const titleSize = Math.min(9, height * 0.09);
    const title = options.title.trim()
      ? text(
          options.title.trim(),
          box.x + pad,
          box.y + pad + titleSize * 0.85,
          titleSize,
          "start",
          true
        )
      : null;
    const rewardHeight = Math.min(34, height * 0.28);
    const rewardRect: Rect = {
      x: r2(box.x + pad),
      y: r2(box.y + height - pad - rewardHeight),
      width: r2(innerWidth),
      height: r2(rewardHeight),
    };
    const area = {
      top: box.y + pad + (title ? titleSize * 1.5 : 0),
      bottom: rewardRect.y - pad * 0.6,
    };
    const pitch = Math.min(
      innerWidth / options.tokens,
      (area.bottom - area.top) * 0.95
    );
    const size = pitch * 0.82;
    const rowWidth = pitch * options.tokens;
    const startX = box.x + (box.width - rowWidth) / 2;
    const cy = (area.top + area.bottom) / 2;
    const marks: ShapeMark[] = Array.from(
      { length: options.tokens },
      (_, index) => ({
        cx: r2(startX + pitch * (index + 0.5)),
        cy: r2(cy),
        size: r2(size),
        number: index + 1,
      })
    );
    const labelSize = Math.min(4.6, rewardHeight * 0.2);
    const value = options.reward.trim()
      ? fitLines(
          measure,
          options.reward.trim(),
          BOLD,
          innerWidth - 8,
          Math.min(8, rewardHeight * 0.34),
          3,
          2
        )
      : null;
    result.push({
      box,
      title,
      marks,
      reward: {
        rect: rewardRect,
        label: text(
          options.rewardLabel,
          rewardRect.x + 4,
          rewardRect.y + labelSize + 2,
          labelSize
        ),
        value: value
          ? value.lines.map((line, k) =>
              text(
                line,
                rewardRect.x + 4,
                rewardRect.y + labelSize + 2 + value.size * 1.15 * (k + 1) + 1,
                value.size,
                "start",
                true
              )
            )
          : [],
      },
    });
  }
  return result;
}
